import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, Params } from '@angular/router';
import { ListViewStateService } from '../services/list-view-state.service';

/**
 * Route data flag: when set on a route config, navigating to the same route with different route
 * params (e.g. `/invoices/A` → `/invoices/B`) destroys and recreates its component instead of
 * reusing it.
 *
 * Angular's default strategy reuses the component whenever the route config is the same, so a page
 * that reads its record once (and provides page-scoped stores/controllers) would keep showing the
 * previous record under the new URL. Recreating also gives the new record fresh page-scoped state,
 * and the route's canDeactivate guard runs exactly once for the transition.
 *
 * Query-param-only changes (`?tab=`, `?mode=`) are unaffected: only route params are compared.
 */
export const RECREATE_ON_PARAM_CHANGE = 'recreateOnParamChange';

/**
 * Route data flag for a list page whose saved view a sidebar menu pick clears (see
 * ListViewStateService.beginReset). While such a pick is in flight the page is rebuilt instead of
 * reused, so picking the list it is already on — or one that only differs by its query string —
 * opens it clean. Routes without the flag keep the default reuse.
 */
export const RECREATE_ON_MENU_PICK = 'recreateOnMenuPick';

@Injectable()
export class RecreateOnParamChangeStrategy extends BaseRouteReuseStrategy {
  private readonly listState = inject(ListViewStateService);

  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }
    if (this.listState.resetting && future.routeConfig?.data?.[RECREATE_ON_MENU_PICK]) {
      return false;
    }
    if (!future.routeConfig?.data?.[RECREATE_ON_PARAM_CHANGE]) {
      return true;
    }
    return sameParams(future.params, curr.params);
  }
}

function sameParams(a: Params, b: Params): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length && aKeys.every((key) => a[key] === b[key]);
}
